import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarList, DistributionBar, InlineTrend, MeterBar } from '../../../components/design-system/analytics';
import { PillTabs, SearchField, SectionCard, TablePagination, formatNumber } from '../../../components/design-system/admin';
import { MarketData } from '../../../common/hooks/use-market-api.hook';
import { GapRow, INTENTS, IntentKey, gapOwners, mergeGaps, scoreLevel } from '../../../modules/vendor/market/market-analysis';
import { gapInsights } from '../../../modules/vendor/market/market-insights';
import { Head, Nothing, OutLink, Reading, SortHead, TableFrame, Term, sortRows, usd, useSort } from './market-shared';
import { UnlockRows } from '../../../components/design-system/plan/UnlockRows';

type SortKey = 'keyword' | 'score' | 'volume' | 'difficulty' | 'cpc' | 'visits';
const PAGE_SIZE = 25;

/**
 * Searches a rival is found for and the vendor is not.
 *
 * The most directly actionable page here: every row is a page they could
 * write. All the rivals' gaps are merged into one list and ranked by an
 * opportunity score (how many search, how hard, whether they want to buy),
 * so the first five are where to start — then everything, filterable by who
 * owns it and why people search.
 */
export const GapsView: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t } = useTranslation();
    const rows = useMemo(() => mergeGaps(data.keywordGaps), [data.keywordGaps]);
    const specs = useMemo(() => gapInsights(rows), [rows]);
    const owners = useMemo(() => gapOwners(rows), [rows]);

    const [rival, setRival] = useState<string>('all');
    const [intent, setIntent] = useState<IntentKey | 'all'>('all');
    const [query, setQuery] = useState('');
    const [brands, setBrands] = useState(false);
    const [page, setPage] = useState(0);
    const { sort, toggle } = useSort<SortKey>('score');
    useEffect(() => setPage(0), [rival, intent, query, brands, sort]);

    const intents = useMemo(() => {
        const totals = new Map<IntentKey, number>();
        for (const r of rows) {
            const k: IntentKey = r.intent ?? 'unknown';
            totals.set(k, (totals.get(k) ?? 0) + r.searchVolume);
        }
        return INTENTS.map((key) => ({ key, value: totals.get(key) ?? 0 })).filter((s) => s.value > 0);
    }, [rows]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = rows.filter(
            (r) =>
                (brands || !r.brand) &&
                (rival === 'all' || r.rivals.some((x) => x.rival === rival)) &&
                (intent === 'all' || (r.intent ?? 'unknown') === intent) &&
                (!q || r.keyword.toLowerCase().includes(q)),
        );
        const get: Record<SortKey, (r: GapRow) => number | string | null> = {
            keyword: (r) => r.keyword,
            score: (r) => r.score,
            volume: (r) => r.searchVolume,
            difficulty: (r) => r.difficulty,
            cpc: (r) => r.cpc,
            visits: (r) => r.rivals.reduce((s, x) => s + x.visits, 0),
        };
        return sortRows(list, get[sort.key], sort.dir);
    }, [rows, rival, intent, query, brands, sort]);

    if (!rows.length) return <Nothing title={t('BUSINESS.GAPS.NONE')} description={t('MARKET.GAPS.NONE')} />;

    const start = rows.filter((r) => !r.brand).slice(0, 5);
    const brandCount = rows.filter((r) => r.brand).length;

    return (
        <div className="flex flex-col gap-6">
            <Reading specs={specs} />

            {start.length > 0 && (
                <section className="flex flex-col gap-3" data-testid="business-start-here">
                    <div>
                        <h2 className="text-sm font-medium">{t('BUSINESS.GAPS.START')}</h2>
                        <p className="mt-0.5 text-xs text-muted-foreground">{t('BUSINESS.GAPS.START_HINT')}</p>
                    </div>
                    <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                        {start.map((r, i) => (
                            <StartCard key={r.keyword} row={r} rank={i + 1} />
                        ))}
                    </ol>
                </section>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
                <SectionCard title={t('BUSINESS.GAPS.BY_RIVAL')} description={t('BUSINESS.GAPS.BY_RIVAL_HINT')}>
                    <BarList
                        items={owners.map((o) => ({
                            key: o.rival,
                            label: o.rival,
                            value: o.volume,
                            secondary: t('BUSINESS.GAPS.SEARCH_COUNT', { count: o.keywords, n: formatNumber(o.keywords) }),
                            onClick: () => setRival(o.rival),
                        }))}
                    />
                </SectionCard>
                <SectionCard title={<Term k="INTENT">{t('BUSINESS.GAPS.BY_INTENT')}</Term>} description={t('BUSINESS.GAPS.BY_INTENT_HINT')}>
                    <DistributionBar columns={2} segments={intents.map((s) => ({ key: s.key, label: t(`BUSINESS.INTENT.${s.key.toUpperCase()}`), value: s.value }))} />
                </SectionCard>
            </div>

            <SectionCard
                flush
                title={t('BUSINESS.GAPS.TABLE')}
                description={t('BUSINESS.GAPS.TABLE_HINT')}
                testId="business-gaps-table"
            >
                <div className="flex flex-col gap-3 px-4 pb-3">
                    {owners.length > 1 && (
                        <PillTabs
                            value={rival}
                            onChange={setRival}
                            ariaLabel={t('BUSINESS.GAPS.BY_RIVAL')}
                            options={[
                                { value: 'all', label: t('BUSINESS.GAPS.ALL_RIVALS'), count: rows.length },
                                ...owners.map((o) => ({ value: o.rival, label: o.rival, count: o.keywords })),
                            ]}
                        />
                    )}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <SearchField value={query} onChange={setQuery} placeholder={t('BUSINESS.SEARCH.FILTER')} />
                        <Select value={intent} onValueChange={(v) => setIntent(v as IntentKey | 'all')}>
                            <SelectTrigger size="sm" className="h-9 w-auto min-w-40" aria-label={t('BUSINESS.TERMS.INTENT.LABEL')}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('BUSINESS.INTENT.ALL')}</SelectItem>
                                {INTENTS.map((i) => (
                                    <SelectItem key={i} value={i}>
                                        {t(`BUSINESS.INTENT.${i.toUpperCase()}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {brandCount > 0 && (
                            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                                <Checkbox checked={brands} onCheckedChange={(v) => setBrands(v === true)} />
                                {t('BUSINESS.GAPS.SHOW_BRANDS', { count: brandCount, n: formatNumber(brandCount) })}
                            </label>
                        )}
                    </div>
                </div>
                <TableFrame className="border-t">
                    <thead className="border-b text-xs text-muted-foreground">
                        <tr>
                            <SortHead align="start" label={t('BUSINESS.COLUMN.KEYWORD')} active={sort.key === 'keyword'} dir={sort.dir} onClick={() => toggle('keyword', 'asc')} />
                            <SortHead label={t('BUSINESS.COLUMN.OPPORTUNITY')} term="OPPORTUNITY" active={sort.key === 'score'} dir={sort.dir} onClick={() => toggle('score')} />
                            <SortHead label={t('BUSINESS.COLUMN.VOLUME')} active={sort.key === 'volume'} dir={sort.dir} onClick={() => toggle('volume')} />
                            <SortHead label={t('BUSINESS.COLUMN.DIFFICULTY')} term="DIFFICULTY" active={sort.key === 'difficulty'} dir={sort.dir} onClick={() => toggle('difficulty', 'asc')} />
                            <Head term="INTENT">{t('BUSINESS.COLUMN.INTENT')}</Head>
                            <SortHead label={t('BUSINESS.COLUMN.CPC')} term="CPC" active={sort.key === 'cpc'} dir={sort.dir} onClick={() => toggle('cpc')} />
                            <Head align="start">{t('BUSINESS.COLUMN.WHO_RANKS')}</Head>
                            <SortHead label={t('BUSINESS.COLUMN.THEIR_VISITS')} active={sort.key === 'visits'} dir={sort.dir} onClick={() => toggle('visits')} />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((r) => (
                            <GapTableRow key={r.keyword} row={r} />
                        ))}
                        {!filtered.length && (
                            <tr>
                                <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                                    {t('BUSINESS.SEARCH.NO_MATCH')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </TableFrame>
                {filtered.length > PAGE_SIZE && <TablePagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} className="border-t" />}
                <UnlockRows count={data.locked?.gaps} noun="gaps" reason="maxGapRows" testId="unlock-gaps" />
            </SectionCard>
        </div>
    );
};

const LEVEL_DOT = { high: 'bg-success', medium: 'bg-warning', low: 'bg-muted-foreground/50' } as const;

const StartCard: React.FC<{ row: GapRow; rank: number }> = ({ row, rank }) => {
    const { t } = useTranslation();
    const best = row.rivals[0];
    const level = scoreLevel(row.score);
    return (
        <li className="flex min-w-0 flex-col gap-3 rounded-xl bg-card p-4 ring-1 ring-foreground/10">
            <div className="flex items-center justify-between gap-2">
                <span className="flex size-6 items-center justify-center rounded-full border text-xs font-medium tabular-nums">{rank}</span>
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className={`size-1.5 rounded-full ${LEVEL_DOT[level]}`} aria-hidden />
                    {t(`BUSINESS.GAPS.LEVEL.${level.toUpperCase()}`)} · <span className="tabular-nums text-foreground">{row.score}</span>
                </span>
            </div>
            <p className="line-clamp-2 min-h-10 text-sm font-medium">{row.keyword}</p>
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground tabular-nums">{t('BUSINESS.COMMON.PER_MONTH', { n: formatNumber(row.searchVolume) })}</span>
                <InlineTrend data={row.trend} width={48} />
            </div>
            <div className="flex flex-col gap-1.5 border-t pt-3 text-xs">
                <span className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('BUSINESS.COLUMN.DIFFICULTY')}</span>
                    <MeterBar value={row.difficulty} invert width="w-10" />
                </span>
                <span className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('BUSINESS.COLUMN.INTENT')}</span>
                    <span>{t(`BUSINESS.INTENT.${(row.intent ?? 'unknown').toUpperCase()}`)}</span>
                </span>
                {best && (
                    <span className="flex min-w-0 items-center justify-between gap-2">
                        <span className="shrink-0 text-muted-foreground">#{best.position ?? '—'}</span>
                        {best.url ? (
                            <OutLink href={best.url} className="text-muted-foreground">
                                {best.rival}
                            </OutLink>
                        ) : (
                            <span className="truncate text-muted-foreground">{best.rival}</span>
                        )}
                    </span>
                )}
            </div>
        </li>
    );
};

const GapTableRow: React.FC<{ row: GapRow }> = ({ row }) => {
    const { t } = useTranslation();
    const visits = row.rivals.reduce((s, x) => s + x.visits, 0);
    return (
        <tr className="border-b last:border-b-0 hover:bg-muted/30">
            <td className="max-w-[260px] p-3 align-top">
                <span className="block truncate font-medium">{row.keyword}</span>
                {row.brand && <span className="mt-0.5 block text-xs text-muted-foreground">{t('BUSINESS.GAPS.BRAND')}</span>}
            </td>
            <td className="p-3 text-end align-top">
                {/* The same cut-offs as the Strong / Medium / Weak words on the cards. */}
                <MeterBar value={row.score} width="w-12" high={55} low={30} />
            </td>
            <td className="p-3 text-end align-top whitespace-nowrap">
                <span className="inline-flex items-center justify-end gap-2">
                    <InlineTrend data={row.trend} className="hidden sm:block" />
                    <span className="tabular-nums">{formatNumber(row.searchVolume)}</span>
                </span>
            </td>
            <td className="p-3 text-end align-top">
                <MeterBar value={row.difficulty} invert width="w-10" />
            </td>
            <td className="p-3 text-end align-top text-xs whitespace-nowrap">{row.intent ? t(`BUSINESS.INTENT.${row.intent.toUpperCase()}`) : '—'}</td>
            <td className="p-3 text-end align-top tabular-nums whitespace-nowrap">{row.cpc != null ? usd(row.cpc, 2) : '—'}</td>
            <td className="max-w-[220px] p-3 align-top">
                <ul className="flex flex-col gap-0.5">
                    {row.rivals.slice(0, 3).map((r) => (
                        <li key={r.rival} className="flex min-w-0 items-center gap-1.5 text-xs">
                            <span className="w-7 shrink-0 tabular-nums text-muted-foreground">#{r.position ?? '—'}</span>
                            {r.url ? (
                                <OutLink href={r.url} title={r.url}>
                                    {r.rival}
                                </OutLink>
                            ) : (
                                <span className="truncate">{r.rival}</span>
                            )}
                        </li>
                    ))}
                </ul>
            </td>
            <td className="p-3 text-end align-top tabular-nums text-muted-foreground">{formatNumber(visits)}</td>
        </tr>
    );
};
